import CircularProgress from "@mui/material/CircularProgress";
import { UserContext } from "App";
import { useContext, useEffect, useRef, useState } from "react";
import { FetchBundleList, getBundleIMS, getProductDetails } from "../../../../../../../../config/Services";
import { useSnackbar } from "../../../../../hooks/SnackbarProvider";
import { useAddToCart } from "../../../../../hooks/useAddToCart";
import useWeighingItemModal from "../../../../../hooks/useWeighingItemModal";
import BundleDetail from "../BundleDetail";
import { getStoreCurrencySymbol } from "../../../../../../../../utils/currency";
import "./index.css";

const PAGE_SIZE = 20;

/* ── Weight-unit label formatting (same as keyboardDropDownModal) ── */
const WEIGHT_UNIT_LABELS = {
  kg: "KG",
  kgs: "KG",
  kilogram: "KG",
  kilograms: "KG",
  ltr: "Liter",
  ltrs: "Liter",
  litre: "Liter",
  litres: "Liter",
  liter: "Liter",
  liters: "Liter",
  l: "Liter",
  gm: "Gram",
  g: "Gram",
  gms: "Gram",
  grm: "Gram",
  gram: "Gram",
  grams: "Gram",
  ml: "ML",
  milliliter: "ML",
  milliliters: "ML",
  millilitre: "ML",
  millilitres: "ML",
  nos: "Nos",
  no: "Nos",
  pc: "Pieces",
  pcs: "Pieces",
  piece: "Pieces",
  pieces: "Pieces",
  each: "Each",
};

const formatWeightUnit = (unit) =>
  unit ? WEIGHT_UNIT_LABELS[unit.toString().trim().toLowerCase()] || unit : "";

/* Same check pattern as keyboardDropDownModal / ProductGrid:
   only the explicit needsWeighingScaleIntegration flag marks a scale product. */
const getWeighingInfo = (b) => {
  const v = b?.variant || b?.variants?.[0] || null;
  const isWeighing =
    b?.needsWeighingScaleIntegration === true || v?.needsWeighingScaleIntegration === true;
  const weightUnit = b?.weightUnit || v?.weightUnit || null;
  return { isWeighing, weightUnit };
};

const BUNDLE_AVATAR_COLORS = ["#6366f1", "#2a71ff", "#8b5cf6", "#0891b2", "#059669"];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtExpDate = (ms) => {
  if (!ms) return null;
  const d = new Date(Number(ms));
  if (isNaN(d.getTime())) return null;
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

/* ── Default bundle icon ── */
function BundleIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="rgba(255,255,255,0.9)"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2.97 12.92A2 2 0 0 0 2 14.63v3.24a2 2 0 0 0 .97 1.71l3 1.8a2 2 0 0 0 2.06 0L12 19v-5.5l-5-3-4.03 2.42Z" />
      <path d="m7 16.5-4.74-2.85" />
      <path d="m7 16.5 5-3" />
      <path d="M7 16.5v5.17" />
      <path d="M12 13.5V19l3.97 2.38a2 2 0 0 0 2.06 0l3-1.8a2 2 0 0 0 .97-1.71v-3.24a2 2 0 0 0-.97-1.71L17 10.5l-5 3Z" />
      <path d="m17 16.5-5-3" />
      <path d="m17 16.5 4.74-2.85" />
      <path d="M17 16.5v5.17" />
      <path d="M7.97 4.42A2 2 0 0 0 7 6.13v4.37l5 3 5-3V6.13a2 2 0 0 0-.97-1.71l-3-1.8a2 2 0 0 0-2.06 0l-3 1.8Z" />
      <path d="M12 8 7.26 5.15" />
      <path d="m12 8 4.74-2.85" />
      <path d="M12 13.5V8" />
    </svg>
  );
}

const BundleList = ({ locId, onAddedToCart }) => {
  const showSnackbar = useSnackbar();
  const { cartItems, setanimationBox } = useContext(UserContext);
  const { addItemWithCheck, addItemDirect } = useAddToCart();
  const { findWeighingItem, renderWeighingModal, weighingStatus } = useWeighingItemModal();

  const CartId = localStorage.getItem("CartId");
  const orgId = localStorage.getItem("orgId");

  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [query, setQuery] = useState("");
  const [selectedBundle, setSelectedBundle] = useState(null);

  /* Per-row status: { [bundleId]: "idle" | "adding" | "success" | "error" } */
  const [rowStatus, setRowStatus] = useState({});

  const setStatus = (id, status) => setRowStatus((prev) => ({ ...prev, [String(id)]: status }));

  /* Flash success/error on the row button, then auto-reset */
  const flashStatus = (id, ok) => {
    setStatus(id, ok ? "success" : "error");
    setTimeout(
      () =>
        setRowStatus((prev) => {
          const next = { ...prev };
          delete next[String(id)];
          return next;
        }),
      ok ? 2200 : 3500
    );
  };

  const fetchBundles = (pageNo = 1, searchQuery = "") => {
    setLoading(true);
    FetchBundleList({
      page: pageNo,
      pageSize: PAGE_SIZE,
      barcode: [],
      query: searchQuery,
      createdBy: [],
      productStatus: [],
      sortByPrice: "DEFAULT",
      sortByCreatedAt: "DESC",
      mergedProductShow: false,
      showBundles: true,
      showOnlyDeActivated: false,
      isService: false,
      storeLocations: [locId],
      storeIds: [orgId]
    })
      .then((res) => {
        const data = res?.data?.data?.data;
        setBundles(data?.data || []);
        setTotalPages(Number(data?.totalPages) || 1);
        setTotalRecords(Number(data?.totalRecords) || 0);
        setPage(pageNo);
      })
      .catch((err) => {
        showSnackbar(
          err?.response?.data?.message || err?.message || "Failed to load bundles",
          "error"
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchBundles(1, "");
  }, [locId]);

  /* Notify parent (cart refresh) after a weighing-modal add completes */
  useEffect(() => {
    if (weighingStatus === "done") onAddedToCart?.();
  }, [weighingStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  /* debounce-lite search — skip on initial mount so we don't double-fetch on load */
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const t = setTimeout(() => fetchBundles(1, query), 400);
    return () => clearTimeout(t);
  }, [query]);

  const handleAddToCart = async (bundle) => {
    const bundleId = String(bundle?.productId || bundle?.id || "");
    const title = bundle?.title || bundle?.name || "Bundle";
    const gtin = bundle?.bundleBarcode;
    const sp = parseFloat(bundle?.bundlePrice || 0);
    const mrp = parseFloat(bundle?.bundlePrice || 0);
    const status = rowStatus[bundleId];

    if (!gtin) {
      showSnackbar(`"${title}" has no bundle barcode`, "error");
      return;
    }
    if (status === "adding" || status === "success") return;

    /* Weighing-scale bundles open the quantity/weight modal instead of a direct add
       (same flow as keyboardDropDownModal). */
    const { isWeighing, weightUnit } = getWeighingInfo(bundle);
    /* Pass the same CartId this component adds with, so the modal targets the
       active cart (hook context cartId can be stale). listType "bundle-list"
       avoids the ODS-specific redux dispatches in the hook. */
    if (
      isWeighing &&
      findWeighingItem(gtin, title, "weighingScale", weightUnit, undefined, CartId, "bundle-list")
    ) {
      return;
    }

    setStatus(bundleId, "adding");

    await addItemWithCheck(
      { cartId: CartId, gtin, locationId: locId, orgId },
      {
        /* Flash the error state for every failure (stale cart, no-cart, API errors).
           onError also shows the snackbar for generic API/network errors. */
        onBeforeError: () => flashStatus(bundleId, false),
        onError: (errMsg) => showSnackbar(errMsg, "error"),
        onSuccess: () => {
          setanimationBox?.(true);
          flashStatus(bundleId, true);
          onAddedToCart?.();
        },
        /* If /add/pos returns batches, auto-pick the first one.
           NOTE: addItemWithCheck does NOT await onBatchSelect internally, so we must
           NOT add a post-await safety-reset below — the nested addItemDirect would
           still be in-flight and a reset here would unlock the button prematurely,
           allowing a duplicate add.  All status transitions are handled inside
           the callbacks; no reset is needed after this await. */
        onBatchSelect: async (dataArr) => {
          const batch = dataArr[0];
          if (!batch) {
            setStatus(bundleId, "idle");
            return;
          }
          await addItemDirect(
            {
              cartId: CartId,
              gtin: batch.gtin || gtin,
              sellingPrice: batch.sellingPrice ?? sp,
              locationId: locId,
              mrp: batch.mrp ?? mrp,
              batchNo: batch.batchNo || batch.batch || "",
              inventoryChecks: "NO",
              isOfferAvailable: false,
              variantName: title,
              orgId,
            },
            cartItems,
            {
              onBeforeError: () => flashStatus(bundleId, false),
              onError: (errMsg) => showSnackbar(errMsg, "error"),
              onSuccess: () => {
                setanimationBox?.(true);
                flashStatus(bundleId, true);
                onAddedToCart?.();
              },
            }
          );
        },
      }
    );
    /* No post-await safety reset — see comment above onBatchSelect. */
  };

  /* ── open bundle detail: navigate immediately with list data, then enrich variants ── */
  const handleViewBundle = async (b) => {
    setSelectedBundle(b);
    if (!b?.bundleBarcode) return;
    try {
      const [pdRes, imsRes] = await Promise.all([
        getProductDetails(b.bundleBarcode, locId),
        getBundleIMS(b.bundleBarcode, locId),
      ]);
      const detail = pdRes?.data?.data;
      /* IMS bundle carries the per-item composition quantity (bundleProducts[].quantity);
         may arrive under res.data.bundle or res.data.data.bundle */
      const imsBody = imsRes?.data?.bundle || imsRes?.data?.data?.bundle || null;
      setSelectedBundle((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        if (detail?.variants?.length) next.variants = detail.variants;
        if (imsBody?.bundleProducts?.length) next.imsBundle = imsBody;
        return next;
      });
    } catch (_) {
      /* silently ignore — list-data variants are still shown */
    }
  };

  /* ── detail view ── */
  if (selectedBundle) {
    return <BundleDetail bundle={selectedBundle} onBack={() => setSelectedBundle(null)} />;
  }

  return (
    <div className="bl-root">
      {renderWeighingModal()}
      {/* ── Header ── */}
      <div className="bl-header">
        <div className="bl-header-left">
          <span className="bl-title">Bundles</span>
          {totalRecords > 0 && <span className="bl-count">{totalRecords}</span>}
        </div>
        <div className="bl-search-wrap">
          <input
            className="bl-search"
            placeholder="Search bundles…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button
              type="button"
              className="bl-search-clear"
              onClick={() => setQuery("")}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      {loading ? (
        <div className="bl-loading">
          <CircularProgress size={22} sx={{ color: "#64748b" }} />
        </div>
      ) : bundles.length === 0 ? (
        <div className="bl-empty">
          <p>No bundles found{query ? ` for "${query}"` : ""}.</p>
          <p className="bl-empty-hint">Create one with the "Add bundle" option.</p>
        </div>
      ) : (
        <div className="bl-list">
          {bundles.map((b, i) => {
            const bundleId = String(b?.productId || b?.id || i);
            const active = b?.bundleEndDate ? Number(b.bundleEndDate) >= Date.now() : true;
            const status = rowStatus[bundleId] || "idle";
            const label = b?.title || b?.name || "Untitled bundle";
            const barcode = b?.bundleBarcode || null;
            const itemCount = b?.variants?.length || 0;
            const rawImg = b?.image || b?.imageUrl || b?.productImage || null;
            const avatarBg =
              BUNDLE_AVATAR_COLORS[(label?.charCodeAt(0) || 0) % BUNDLE_AVATAR_COLORS.length];
            const { isWeighing, weightUnit } = getWeighingInfo(b);
            const trimmedQuery = query.trim().toLowerCase();
            const matchedVariant = trimmedQuery
              ? (b?.variants || [])?.find(
                  (v) =>
                    v?.name?.toLowerCase().includes(trimmedQuery) ||
                    (v?.barcodes || [])?.some((bc) => bc?.toLowerCase().includes(trimmedQuery))
                )
              : null;

            return (
              <div key={bundleId} className={`bl-row${!active ? " bl-row--expired" : ""}`}>
                {/* Image / avatar */}
                <div
                  className="bl-row-img"
                  style={!rawImg ? { background: avatarBg } : undefined}
                  onClick={() => handleViewBundle(b)}
                  title="View bundle details"
                >
                  {rawImg ? (
                    <img
                      src={rawImg}
                      alt={label}
                      className="bl-row-img-el"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        e.currentTarget.parentElement.style.background = avatarBg;
                      }}
                    />
                  ) : (
                    <BundleIcon />
                  )}
                </div>

                {/* Info */}
                <div
                  className="bl-row-body"
                  onClick={() => handleViewBundle(b)}
                  title="View bundle details"
                >
                  <p className="bl-row-name">{label}</p>
                  {barcode && <p className="bl-row-barcode">{barcode}</p>}
                  <div className="bl-row-meta">
                    {b?.bundlePrice != null && (
                      <span className="bl-row-price">
                        MRP {getStoreCurrencySymbol()}
                        {parseFloat(b.bundlePrice).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    )}
                    {b?.externalSalesChannels?.[0]?.salePrice != null && (
                      <span className="bl-row-price bl-row-price--sp">
                        SP {getStoreCurrencySymbol()}
                        {parseFloat(b.externalSalesChannels[0].salePrice).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    )}
                    <span className="bl-chip">
                      {itemCount} item{itemCount !== 1 ? "s" : ""}
                    </span>
                    {matchedVariant && (
                      <span
                        className="bl-chip bl-chip--match"
                        title={`Matched: ${matchedVariant?.name}`}
                      >
                        {matchedVariant.name}
                      </span>
                    )}
                    {isWeighing && (
                      <span
                        className="product-search-badge-scale"
                        data-testid={`status-weighing-scale-bundle-${i}`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="product-search-scale-icon"
                          aria-hidden="true"
                        >
                          <circle cx="12" cy="5" r="3"></circle>
                          <path d="M6.5 8a2 2 0 0 0-1.905 1.46L2.1 18.5A2 2 0 0 0 4 21h16a2 2 0 0 0 1.925-2.54L19.4 9.5A2 2 0 0 0 17.48 8Z"></path>
                        </svg>
                        Scale
                      </span>
                    )}
                    {isWeighing && weightUnit && (
                      <span
                        className="product-search-badge-weight-unit"
                        data-testid={`status-weight-unit-bundle-${i}`}
                      >
                        {formatWeightUnit(weightUnit)}
                      </span>
                    )}
                    {b?.bundleEndDate && (
                      <span
                        className={`bl-expiry-pill${!active ? " bl-expiry-pill--expired" : ""}`}
                      >
                        Exp: {fmtExpDate(b.bundleEndDate)}
                      </span>
                    )}
                  </div>
                </div>

                {/* ADD button with inline flash states */}
                <button
                  type="button"
                  className={[
                    "bl-row-add-btn",
                    status === "success" && "bl-row-add-btn--success",
                    status === "error" && "bl-row-add-btn--error",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  disabled={!active || status === "adding" || status === "success"}
                  title={active ? `Add "${label}" to cart` : "Bundle expired"}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToCart(b);
                  }}
                >
                  {status === "adding" ? (
                    <CircularProgress size={13} sx={{ color: "#fff" }} />
                  ) : status === "success" ? (
                    <>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Added
                    </>
                  ) : status === "error" ? (
                    <>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                      </svg>
                      Failed
                    </>
                  ) : (
                    <>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M12 5v14" />
                        <path d="M5 12h14" />
                      </svg>
                      Add
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="bl-pagination">
          <button
            type="button"
            className="bl-page-btn"
            disabled={page <= 1 || loading}
            onClick={() => fetchBundles(page - 1, query)}
          >
            ‹ Prev
          </button>
          <span className="bl-page-info">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className="bl-page-btn"
            disabled={page >= totalPages || loading}
            onClick={() => fetchBundles(page + 1, query)}
          >
            Next ›
          </button>
        </div>
      )}
    </div>
  );
};

export default BundleList;
